import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Button } from "../button/button";

export type ComboboxItem<T> = T & {
  value: string;
  label: string;
};

export type ComboboxProps<T> = {
  items: ComboboxItem<T>[];
  onSelect?: (value: string) => void;
  renderGroup: (children: React.ReactNode) => React.ReactNode;
  renderItem: (item: ComboboxItem<T>, selected: boolean) => React.ReactNode;
  title?: string;
  popOverClassName?: string;
  isSearchable?: boolean;
  container?: HTMLElement | null;
  value: string;
} & (
  | { isSearchable: true; searchPlaceholder: string }
  | { isSearchable?: false; searchPlaceholder?: never }
);

export function Combobox<T>(props: ComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);

  const handleSelect = (value: string) => {
    setOpen(false);
    props.onSelect?.(value);
  };

  const selectedItemLabel = props.value
    ? props.items.find((item) => item.value === props.value)?.label
    : props.title || "Select item...";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between p-3 border border-input bg-white text-foreground font-normal shadow-none"
        >
          {selectedItemLabel}
          <ChevronDown
            className={cn(
              "ml-2 h-5 w-5 shrink-0 opacity-50 transition-transform duration-200",
              open && "-rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        container={props.container}
        side="bottom"
        className={cn("p-0 overflow-y-auto ", props.popOverClassName)}
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command>
          {props.isSearchable && (
            <CommandInput placeholder={props.searchPlaceholder} />
          )}
          <CommandList>
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {/* prevents first item to be selected by default*/}
              <CommandItem value="-" className="hidden" />
              {props.renderGroup(
                props.items.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    keywords={[item.label]}
                    onSelect={(currentValue) =>
                      handleSelect(
                        currentValue === props.value ? "" : currentValue
                      )
                    }
                  >
                    {props.renderItem(item, props.value === item.value)}
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
